const API_BASE = '/api/v1';

let _getToken = () => null;
let _onUnauthorized = () => {};
// Imperative token refresh, wired by AuthContext. Returns a fresh ID token on
// success or null if the refresh failed / isn't possible. Used to recover from
// a 401 in-flight instead of immediately logging the user out.
let _refreshToken = async () => null;
// In-flight shared refresh promise (single-flight; see the 401 branch).
let _refreshInFlight = null;
// Step-up handler, wired by AuthContext. Called when the backend returns a
// `step_up_required` 403 on a critical action (delete/export) because the
// session's auth is older than the step-up window (Tier 3 session hardening).
// It redirects to the Portal for a fresh password+MFA re-auth.
let _onStepUp = () => {};

/**
 * Wire up auth integration. Called once from AuthContext on mount.
 */
export function configureApi(tokenGetter, unauthorizedCallback, refreshTokenFn, stepUpFn) {
  _getToken = tokenGetter;
  _onUnauthorized = unauthorizedCallback;
  if (refreshTokenFn) _refreshToken = refreshTokenFn;
  if (stepUpFn) _onStepUp = stepUpFn;
}

// ── Auth-failure circuit breaker ────────────────────────────────────────────
// Sleep-wake left tabs polling every 4s with a stale token; the edge counts
// auth failures per IP (staging 8/30s, prod 5/15min → 30-min lock) and the
// lock RE-TRIPS on expiry while any stale tab keeps polling — a self-DoS that
// locked real users out twice in one day. After an unrecovered 401 (or any
// 429) the breaker opens: requests fail fast LOCALLY, zero network, so a
// stale tab can never re-trip the lock. Two ways forward:
//   - a NEW token (fresh login / successful refresh) always earns one real
//     attempt, even while the window is open — success resets everything;
//   - the same token gets one probe per window; consecutive failures grow the
//     window 30s → ×3 → 30min cap, which stays under prod's 5/15min budget.
let _authBackoffUntil = 0;
let _authStrikes = 0;
// The credential that failed ('*' = block regardless of token, e.g. an IP
// lock 429 where even fresh credentials are rejected at the edge).
let _authFailedToken = null;
// Terminal dead-session latch. Set when the IdP definitively rejects our
// refresh token (revoked/expired — not a blip): unlike the backoff window,
// this never expires and ignores the "new credentials" escape hatch, because
// a dead session can keep minting fresh-looking tokens that would each earn
// a real attempt and re-trip the per-IP auth lock. Cleared only by an actual
// new sign-in (Portal handoff / login popup).
let _sessionDead = false;

export function markSessionDead() { _sessionDead = true; }
// True once AuthContext has a credential to send. Pollers that mount before
// auth is restored (App-level providers keyed off the URL) wait on this.
export function hasAuthToken() { const t = _getToken(); return Boolean(t); }
export function clearSessionDead() { _sessionDead = false; }
// A genuine NEW sign-in must start from a clean slate: dead-session latch,
// strike counter, backoff window and failed-credential marker all cleared.
// Before this existed, breaker state could survive a fresh portal handoff
// and freeze the tab on "Requests are paused" (2026-07-20).
export function resetAuthState() {
  _sessionDead = false;
  _resetAuthBackoff();
}
const _AUTH_BACKOFF_BASE_MS = 30_000;
const _AUTH_BACKOFF_FACTOR = 3;
const _AUTH_BACKOFF_CAP_MS = 30 * 60_000;

function _noteAuthFailure(token, retryAfterSec) {
  _authStrikes += 1;
  const backoff = retryAfterSec > 0
    ? retryAfterSec * 1000
    : Math.min(
        _AUTH_BACKOFF_BASE_MS * _AUTH_BACKOFF_FACTOR ** (_authStrikes - 1),
        _AUTH_BACKOFF_CAP_MS,
      );
  _authBackoffUntil = Date.now() + backoff;
  _authFailedToken = token || '*';
}

function _resetAuthBackoff() {
  _authBackoffUntil = 0;
  _authStrikes = 0;
  _authFailedToken = null;
}

function _authBlocked(token) {
  if (_sessionDead) return true;
  if (Date.now() >= _authBackoffUntil) return false;
  if (_authFailedToken !== '*' && token && token !== _authFailedToken) {
    return false; // new credentials — worth one real attempt
  }
  return true;
}

function _authBackoffError() {
  const e = new Error(
    'Requests are paused after repeated authentication failures — sign in again or wait a moment.',
  );
  e.status = 401;
  e.authBackoff = true;
  return e;
}

// `_overrideToken`, when set, is the freshly-refreshed token used for the
// single post-401 retry. Its presence also marks "already retried" so we
// never loop. We pass it explicitly rather than re-reading _getToken() because
// the AuthContext token state update is async and may not have landed yet.
export function currentCaseContext() {
  const m = /\/case\/([0-9a-f-]{36})(?:[/?#]|$)/i.exec(window.location.pathname || '');
  return m ? m[1] : null;
}

async function request(method, path, { body, params } = {}, _overrideToken) {
  const url = new URL(API_BASE + path, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v != null) url.searchParams.set(k, String(v));
    });
  }

  const headers = {};
  if (body) headers['Content-Type'] = 'application/json';
  // The case the user has open, for routes that carry it in a body or query
  // string (uploads, Ask Arc): lets a case shared with an outside consultant
  // resolve to the right organization. A hint only; the server checks the grant.
  const caseCtx = currentCaseContext();
  if (caseCtx) headers['X-Case-Context'] = caseCtx;

  const token = _overrideToken || _getToken();
  if (_authBlocked(token)) throw _authBackoffError();
  if (token && token !== 'dev-mode') {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.ok) _resetAuthBackoff();

  // 401 = bad/expired credentials. Before kicking to login, try a single
  // silent token refresh + retry — this is what keeps an actively-working
  // user (e.g. mid Ask-Arc conversation) from being bounced to the login
  // screen the moment their 1h Firebase ID token lapses. Only if the refresh
  // genuinely fails do we log out.
  // 403 = authenticated but not authorized for this resource → propagate
  // as a normal error so callers can handle (e.g. ops-admin probe that
  // expects 403 for non-ops users, or per-endpoint role gates). The one
  // exception is a structured `step_up_required` envelope (Tier 3): the
  // session is valid but too old for a critical action, so we trigger the
  // re-auth handler and throw a marked error rather than a raw 403.
  if (res.status === 401) {
    // No credential was sent (auth not restored yet — e.g. a case-level
    // poller mounted above the route guard on a deep link / reload). The
    // session did not fail; logging out here wiped a valid token and sent
    // the person through Google again with no prompt (P1.11 click-through,
    // 2026-09-16, three times). Fail this call quietly; the poller retries.
    if (!token) {
      const e = new Error('Unauthorized');
      e.status = 401;
      e.noCredential = true;
      throw e;
    }
    if (!_overrideToken) {
      // Single-flight: concurrent polls share ONE refresh instead of each
      // hammering the IdP with its own attempt.
      if (!_refreshInFlight) {
        _refreshInFlight = Promise.resolve()
          .then(() => _refreshToken())
          .catch(() => null)
          .finally(() => { _refreshInFlight = null; });
      }
      const newToken = await _refreshInFlight;
      if (newToken) return request(method, path, { body, params }, newToken);
    }
    _noteAuthFailure(token);
    // A revoked session (ops or an org admin removed the person — AW plan
    // D7) must not be bounced straight back into Google sign-in as if it
    // had merely expired: the login page tells them their access was
    // removed instead.
    let reason = 'expired';
    try {
      if (/revoked/i.test(await res.clone().text())) reason = 'revoked';
    } catch { /* body unreadable — treat as expired */ }
    // Pass the credential that actually failed so logout can tell "this
    // session died" from "a stale tab 401'd while a fresh session exists"
    // (cross-tab guard in AuthContext.logout).
    _onUnauthorized(token, reason);
    const e = new Error('Unauthorized');
    e.status = 401;
    throw e;
  }

  if (res.status === 429) {
    // Rate limited / IP auth-lock at the edge. Back off hard — every retry
    // while locked re-trips the lock's expiry counter.
    const retryAfter = Number(res.headers.get('Retry-After')) || 0;
    _noteAuthFailure(null, retryAfter);
    const e = new Error('Too many requests — backing off.');
    e.status = 429;
    throw e;
  }

  if (res.status === 403) {
    // The backend wraps errors into a flat envelope: { error, message, ... }.
    // A `step_up_required` envelope (Tier 3) carries the structured fields at
    // the top level, so branch on `body.error`, not a nested `detail`.
    const body = await res.json().catch(() => ({}));
    if (body && body.error === 'step_up_required') {
      _onStepUp(body);
      const e = new Error('Re-authentication required to continue.');
      e.status = 403;
      e.stepUp = true;
      e.detail = body;
      throw e;
    }
    const msg = (body && (body.message || body.detail)) || `HTTP 403`;
    const e = new Error(typeof msg === 'string' ? msg : `HTTP 403`);
    e.status = 403;
    throw e;
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    // Backend wraps errors into a flat envelope: { error, message, ... }.
    // Prefer the human `message`, fall back to legacy `detail`. Expose the
    // machine `error` code + raw body so callers can branch (e.g. a 402
    // ALLOTMENT_EXCEEDED renders the "allowance reached" banner).
    const e = new Error(err.message || err.detail || `HTTP ${res.status}`);
    e.status = res.status;
    e.code = err.error;
    e.detail = err;
    throw e;
  }

  if (res.status === 204) return null;
  return res.json();
}

/** Return the current Firebase ID token.
 *
 * WARNING: Never put this in a URL — it leaks to access logs and Referer.
 * For element src attributes (img/video/audio/iframe), use
 * `mintViewerToken(documentId)` to get a short-lived, document-scoped token.
 */
export function getToken() { return _getToken(); }

// Short-lived viewer tokens — cache by document id. Tokens are 5 min TTL
// on the server; we refresh ~30s before expiry to avoid edge-of-expiry races.
const _viewerTokenCache = new Map(); // documentId -> { token, expires_at }
const _VIEWER_TOKEN_REFRESH_MARGIN_SEC = 30;

/**
 * Mint (or return cached) a short-lived viewer token for the given document.
 *
 * Used for routes loaded by HTML media elements (<video>, <audio>, <img>)
 * and iframes where the browser cannot send an Authorization header.
 * Server-side enforces:
 *   - the caller (at mint time) belongs to the doc's organization
 *   - the token is bound to this specific document id
 *   - the token expires after VIEWER_TOKEN_TTL_SECONDS (~5 min)
 */
export async function mintViewerToken(documentId) {
  const cached = _viewerTokenCache.get(documentId);
  const now = Math.floor(Date.now() / 1000);
  if (cached && cached.expires_at - now > _VIEWER_TOKEN_REFRESH_MARGIN_SEC) {
    return cached.token;
  }
  const res = await request('POST', `/documents/${documentId}/viewer-token`);
  if (!res || !res.token) throw new Error('Viewer token mint failed');
  _viewerTokenCache.set(documentId, res);
  return res.token;
}

export const api = {
  get: (path, params) => request('GET', path, { params }),
  post: (path, body) => request('POST', path, { body }),
  patch: (path, body) => request('PATCH', path, { body }),
  put: (path, body) => request('PUT', path, { body }),
  del: (path, body) => request('DELETE', path, { body }),
  /** Raw fetch with auth headers — returns the Response object (for binary
   *  data / file downloads). Optional JSON body for POST-shaped downloads. */
  raw: async (method, path, body) => {
    const url = new URL(API_BASE + path, window.location.origin);
    const headers = {};
    const token = _getToken();
    if (_authBlocked(token)) throw _authBackoffError();
    if (token && token !== 'dev-mode') headers['Authorization'] = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    return fetch(url, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  },
  /** Upload FormData (multipart) — skips JSON Content-Type */
  upload: async (path, formData, _overrideToken) => {
    const url = new URL(API_BASE + path, window.location.origin);
    const headers = {};
    const token = _overrideToken || _getToken();
    if (_authBlocked(token)) throw _authBackoffError();
    if (token && token !== 'dev-mode') headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url, { method: 'POST', headers, body: formData });
    if (res.ok) _resetAuthBackoff();
    if (res.status === 401) {
      if (!_overrideToken) {
        if (!_refreshInFlight) {
          _refreshInFlight = Promise.resolve()
            .then(() => _refreshToken())
            .catch(() => null)
            .finally(() => { _refreshInFlight = null; });
        }
        const newToken = await _refreshInFlight;
        if (newToken) return api.upload(path, formData, newToken);
      }
      _noteAuthFailure(token);
      _onUnauthorized();
      const e = new Error('Unauthorized'); e.status = 401; throw e;
    }
    if (res.status === 429) {
      const retryAfter = Number(res.headers.get('Retry-After')) || 0;
      _noteAuthFailure(null, retryAfter);
      const e = new Error('Too many requests — backing off.'); e.status = 429; throw e;
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      const e = new Error(err.detail || `HTTP ${res.status}`); e.status = res.status; throw e;
    }
    if (res.status === 204) return null;
    return res.json();
  },
};

/**
 * Fetch health endpoint to check dev_mode status.
 */
export async function fetchHealth() {
  const res = await fetch('/health');
  return res.json();
}
